/**
 * Read-only viewer for the raw message source (RFC 5322) with copy and .eml download
 * (DESIGN.md T10 "View source"). Large messages are shown cut (the server streams only the first
 * bytes); the download always has the whole message.
 */
import { useI18n } from '@platform/i18n';
import { CopyIcon, DownloadIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { Button } from '../components/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/dialog';
import { Alert, Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { ErrorState } from '../composites/states';
import { formatBytes } from '../lib/format';

export interface MessageSourceView {
  text: string;
  origin: 'imap' | 'spliced' | 'ews' | 'reconstructed';
  /** Full size in bytes. */
  size: number;
  truncated: boolean;
}

export interface MessageSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject?: string;
  source?: MessageSourceView;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Link to the full .eml (plain GET with the session cookie). */
  downloadUrl: string;
}

export function MessageSourceDialog({ open, onOpenChange, subject, source, loading, error, onRetry, downloadUrl }: MessageSourceDialogProps) {
  const { t, formatNumber } = useI18n();
  const copy = () => {
    if (source) void navigator.clipboard.writeText(source.text).then(() => toast.success(t('mailSource.copied')));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-full max-w-none flex-col gap-3 rounded-none p-4 sm:h-11/12 sm:max-w-5xl sm:rounded-lg sm:p-6">
        <DialogHeader className="pr-8">
          <DialogTitle>{t('mailSource.title')}</DialogTitle>
          <DialogDescription className="truncate">{subject}</DialogDescription>
        </DialogHeader>
        {error ? (
          <ErrorState onRetry={onRetry} />
        ) : loading || !source ? (
          <div className="flex flex-1 flex-col gap-2" aria-busy="true" aria-label={t('mailSource.loading')}>
            {Array.from({ length: 10 }, (_, i) => (
              <Skeleton key={i} className="h-3 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">{t('mailSource.size', { size: formatBytes(source.size, formatNumber) })}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copy}>
                  <CopyIcon />
                  {t('mailSource.copy')}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={downloadUrl} download>
                    <DownloadIcon />
                    {t('mailSource.download')}
                  </a>
                </Button>
              </div>
            </div>
            {source.origin !== 'imap' && (
              <Alert variant={source.origin === 'reconstructed' ? 'warning' : 'info'}>
                {source.origin === 'reconstructed' ? <TriangleAlertIcon /> : <InfoIcon />}
                <span>{t(`mailSource.origin.${source.origin}`)}</span>
              </Alert>
            )}
            {source.truncated && (
              <Alert variant="info">
                <InfoIcon />
                <span>
                  {t('mailSource.truncated', {
                    shown: formatBytes(new TextEncoder().encode(source.text).length, formatNumber),
                    total: formatBytes(source.size, formatNumber),
                  })}
                </span>
              </Alert>
            )}
            <pre
              tabIndex={0}
              aria-label={t('mailSource.label')}
              className="min-h-0 flex-1 overflow-auto rounded-md border bg-muted p-3 font-mono text-xs break-all whitespace-pre-wrap outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              {source.text}
            </pre>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
